<template>
    <!-- Loading state -->
    <div class="gmf-cform-v2 gmf-context gmf-cform-v2-loading-container" v-if="isLoading">
        <div class="gmf-cform-v2-loading-content">
            <div class="gmf-cform-v2-loading-spinner">
                <img src="https://cdn.gamifiera.com/static/icons/loading_simple.svg" />
            </div>
        </div>
    </div>

    <!-- Main form content -->
    <div class="gmf-cform-v2 gmf-context" v-else-if="!isLoading">

        <!-- Login usp -->
        <template v-if="step === -1">
            <div class="gmf-bubbly-usp-title"
                v-text="md(t('comments.bubbly_usp_title.' + state.merchantId) || t('comments.bubbly_usp_title.default'))" />
            <div class="gmf-bubbly-usp-text"
                v-html="md(t('comments.bubbly_usp_text.' + state.merchantId) || t('comments.bubbly_usp_text.default'))" />
            <div class="gmf-bubbly-usp-image" />
            <div class="gmf-bubbly-usp-footer gmf-center gmf-mt-5">
                <button class="gmf-btn gmf-btn-primary" v-text="t('comments.log_in')" @click="requestLogin"></button>
                <div class="gmf-mt-4" v-if="guestCommentsAllowed"><a class="gmf-text-small gmf-link"
                        v-text="t('comments.call_to_action_guest.product_review')" @click="step = 0" /></div>
            </div>
        </template>

        <!-- Sending -->
        <template v-else-if="isSending">
            <h2 class="gmf-header">{{ t('comments.comment_type_is_sending.' + commentType) }}</h2>
            <div class="gmf-cform-v2-loading"><img src="https://cdn.gamifiera.com/static/icons/loading_simple.svg" /></div>
        </template>

        <!-- Success screen -->
        <AfterReviewForm :commentType="commentType" :tokenProfile="tokenProfile" :reviewToken="token" v-if="success" @close="close" :targetId="formData.target"></AfterReviewForm>

        <!-- Form -->
        <template v-else-if="step >= 0">
            <CompactProfileCard v-if="profile" :profile="profile" hideStats hideAttributes />
            <div v-else class="gmf-cform-v2-guest-header">
                <!--<Avatar />-->
                <div class="gmf-cform-v2-guest-header-right">
                    <div class="gmf-cform-v2-section">
                        <FieldHeader field="name" :fieldType="fieldTypes.name" :validationError="validationResult.name"
                            :commentType="commentType" />
                        <TextInput v-model="formData.name" :ariaLabel="nameLabel" />
                    </div>
                    <div class="gmf-cform-v2-section" v-if="!commentTypeConfig.skipGuestEmail">
                        <FieldHeader field="email" :fieldType="fieldTypes.email" :validationError="validationResult.email"
                            :commentType="commentType" />
                        <TextInput v-model="formData.email" :ariaLabel="emailLabel" />
                    </div>
                </div>
            </div>
            <VerticalSeparator>{{ header }}</VerticalSeparator>

            <div v-if="targetInfo && step == 0 && !fields.includes('target')" class="gmf-cform-v2-target-info">
                <div class="gmf-cform-v2-target-info-image" v-if="targetInfo.imageUrl"><img :src="targetInfo.imageUrl"></div>
                <div class="gmf-cform-v2-target-info-name" v-max-lines="2">{{ targetInfo.name }}</div>
            </div>

            <div v-for="field in fields" :key="field" class="gmf-cform-v2-section"
                :class="'gmf-cform-v2-section-field-' + field">
                <FieldHeader :field="field" :fieldType="fieldTypes[field]" :validationError="validationResult[field]"
                    :commentType="commentType" />
                <div class="gmf-text-xs gmf-mb-2" v-if="fieldTypes[field].subtitle" v-text="fieldTypes[field].subtitle"></div>
                <FieldInput :field="field" :fieldType="fieldTypes[field]" v-model="formData[field]" :commentType="commentType" />
            </div>

            <div class="gmf-cform-v2-footer">
                <Guidelines :commentType="commentType" v-model="hasApprovedGuidelines" />

                <div class="gmf-cform-v2-footer-btns">
                    <button class="gmf-btn gmf-btn-secondary" v-if="step > 0" @click="step--">
                        {{ t('comments.form_v2.' + commentType + '.previous') || t('comments.form_v2.default.previous') }}
                    </button>
                    <button class="gmf-btn gmf-btn-primary" v-if="step == steps.length - 1" :disabled="!entireFormIsValid"
                        @click="postComment">
                        {{ t('comments.form_v2.' + commentType + '.finish') || t('comments.form_v2.default.finish') }}</button>
                    <button class="gmf-btn gmf-btn-primary" v-else @click="step++" :disabled="!stepIsComplete">
                        {{ t('comments.form_v2.' + commentType + '.next') || t('comments.form_v2.default.next') }}
                    </button>
                </div>

                <div class="gmf-alert gmf-alert-error" v-if="errorMessage">{{ errorMessage }}</div>

            </div>
        </template>

    </div>
</template>

<style>
.gmf-cform-v2 {
    width: 600px;
    max-width: 100%;

    display: flex;
    flex-direction: column;
    gap: var(--gmf-p-5);
}

.gmf-cform-v2 .gmf-avatar {
    width: 80px;
    height: 80px;
}

.gmf-cform-v2-guest-header {
    display: flex;
    gap: var(--gmf-p-5);
    align-items: center;
}

.gmf-cform-v2-guest-header-right {
    flex-direction: column;
    display: flex;
    gap: var(--gmf-p-3);
    flex-grow: 1;
}

.gmf-cform-v2-footer {
    display: flex;
    flex-direction: column;
    gap: var(--gmf-p-4);
}

.gmf-cform-v2-footer-btns {
    display: flex;
    gap: var(--gmf-p-3);
    justify-content: center;
}
.gmf-cform-v2-loading {
    text-align: center;
}

.gmf-cform-v2-loading-container {
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.gmf-cform-v2-loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--gmf-p-3);
}

.gmf-cform-v2-loading-spinner img {
    width: 32px;
    height: 32px;
}

.gmf-cform-v2-target-info {
    display: flex;
    gap: var(--gmf-p-3);
    align-items: center;
}

.gmf-cform-v2-target-info-image {
    flex-shrink: 0;
}
.gmf-cform-v2-target-info-image img {
    width: 64px;
    height: 64px;
    object-fit: contain;
    border-radius: 8px;
}
</style>

<script>
import CompactProfileCard from '@/components/CompactProfileCard';
import VerticalSeparator from '@/components/VerticalSeparator.vue';

import state from '@/state';
// import Avatar from '@/components/Avatar.vue';
import FieldInput from './FieldInput';
import TextInput from '@/components/input/TextInput';
import FieldHeader from './FieldHeader';

import validate from './validate';

import md from '@/utils/md';
import Guidelines from './Guidelines';
import callApi from '@/utils/callApi';

import postComment from '@/utils/postComment';

import requestLogin from '@/utils/requestLogin';
import getMyProfile from '@/utils/getMyProfile';


import modal from '@/utils/modal';
import advancedString from '@/utils/advancedString';
import hrFileSize from '@/utils/hrFileSize';

import maxLines from '@/utils/directives/maxLines';


// import OrderedProductList from '@/components/OrderedProductList';

import AfterReviewForm from './AfterReviewForm.vue';

const t = state.t;

//const MIN_PROCON_LENGTH = 2;
const MAX_PROCON_LENGTH = 100;
const MAX_PROS_OR_CONS = 5;

//const MAX_PRICE_LENGTH = 50;
//const MAX_LOCATION_LENGTH = 50;

export default {
    name: 'CommentFormV2',
    directives: {
        'max-lines': maxLines,
    },
    components: {
        CompactProfileCard,
        VerticalSeparator,
        // Avatar,
        FieldInput,
        TextInput,
        FieldHeader,
        Guidelines,
        AfterReviewForm,
        // CustomerAttributeEditor,
        // OrderedProductList
    },
    methods: {
        t,
        md,
        requestLogin,
        async postComment() {
            if (this.isSending) {
                return;
            }
            this.errorMessage = null;

            const fd = this.formData;

            const ratings = {};
            const cnf = this.commentTypeConfig;

            for (const rt of [...cnf.requiredRatings, ...cnf.optionalRatings]) {
                ratings[rt] = fd['ratings.' + rt];
            }

            this.isSending = true;

            const { error } = await postComment(this.commentType, fd.target, {
                parentId: this.parentCommentId,
                title: fd.title,
                text: fd.text,
                pros: fd.pros,
                cons: fd.cons,
                ratings,
                files: fd.images,
                customerName: fd.name,
                email: fd.email,
                price: fd.price,
                location: fd.location,
                token: this.token,
            });

            this.isSending = false;

            if (!error) {
                this.success = true;
            } else {
                this.errorMessage = t('error_codes.' + error.code) || t('error_codes.default');
            }
        },
        async init(commentType, targetId, options = {}, parentCommentId = null, skipGuestUsp = false, namedArgs = {}) {
            const {
                product_score,
                token,
            } = namedArgs;

            // Prefilled score
            if (product_score) {
                this.formData['ratings.product_score'] = parseInt(product_score);
            }

            this.onCommentCreated = options.onCommentCreated;
            this.commentType = commentType;
            this.formData.target = targetId;
            this.parentCommentId = parentCommentId;
            this.skipGuestUsp = skipGuestUsp;
            this.token = token;

            let profile = null;

            if (this.token) {
                const res = await callApi('GET', '/v2/review-token/' + this.token);
                const data = await res.json();

                if (data.token) {
                    const res2 = await callApi('GET', '/v1/client/profile/' + data.token.merchantCustomerId);
                    profile = await res2.json();
                    
                    this.tokenProfile = profile;
                    this.tokenProfile.products = data.token.products;
                }
            }

            this.isSending = false;

            // Load selectable variants
            this.productHasSize = false;
            if (!this.parentCommentId && !this.tokenProfile) { // Can't select variants for replies or token users
                if (targetId && this.commentType.startsWith('product')) { // @TODO better type checking
                    const res = await callApi('GET', '/v1/client/comment-product-info/' + targetId);
                    const data = await res.json();

                    this.targetInfo = data;
                    this.productHasSize = data.meta && !!(data.meta.size || data.meta.categoryHasSizes);
                    this.selectableTargets = data.variants;
                }
                if (this.commentType === 'social_post') {
                    const res = await callApi('GET', '/v1/social-boards');
                    const data = await res.json();
                    this.selectableTargets = data.boards.map(b => ({
                        id: b.id,
                        name: b.name,
                    }));
                }
            }

            if (! profile) {
                profile = await getMyProfile();
            }

            this.step = -1;
            if (! state.merchantConfig.commentForm.showLoginUsp) {
                this.step = 0;
            } else if (profile) {
                this.step = 0;
            } else if (this.skipGuestUsp && this.guestCommentsAllowed) {
                this.step = 0;
            }

            if (! this.guestCommentsAllowed && ! profile && ! state.merchantConfig.commentForm.showLoginUsp) {
                this.close();
                setTimeout(()=>{
                    modal.error(t('comments.log_in_to_write_a.' + this.commentType));
                },0);
                return;
            }

            if (this.selectableTargets && this.selectableTargets.length > 0) {
                // check if formdata.target is in selectableTargets
                if (!this.selectableTargets.find(t => t.id === this.formData.target)) {
                    this.formData.target = null;
                }
                if (this.selectableTargets.length === 1) {
                    this.formData.target = this.selectableTargets[0].id;
                }
            }

            this.isLoading = false;
        },
        async close() {
            this.onCommentCreated();
        },
    },
    data() {
        return {
            skipGuestUsp: false,

            parentCommentId: null,
            errorMessage: null,
            success: false,

            state,

            onCommentCreated: null,

            commentType: null,
            formData: {
                target: null,
                name: null,
                email: null,
                title: null,
                text: null,
                pros: [],
                cons: [],
                images: [],
            },

            targetInfo: null,
            hasApprovedGuidelines: true,
            step: -1,
            guidelinesModalOpen: false,

            selectableTargets: [],
            isLoading: true,

            isSending: false,

            token: null,
            tokenProfile: null,

            successStep: 0,

            productHasSize: false,
        };
    },
    computed: {
        nameLabel() {
            return t('comments.form_v2.' + this.commentType + '.name.title') || t('comments.form_v2.default.name.title');
        },
        emailLabel() {
            return t('comments.form_v2.' + this.commentType + '.email.title') || t('comments.form_v2.default.email.title');
        },
        guestCommentsAllowed() {
            return this.commentTypeConfig.allowGuests;
        },
        header() {
            const headerOptions = [];
            if (this.step > 0) {
                headerOptions.push(
                    'comments.form_v2.' + this.commentType + '.header_2',
                    'comments.form_v2.default.header_2',
                );
            }
            headerOptions.push(
                'comments.form_v2.' + this.commentType + '.header',
                'comments.form_v2.default.header',
            );

            for (const opt of headerOptions) {
                const translated = t(opt);
                if (translated) {
                    return translated;
                }
            }

            return null;
        },
        stepIsComplete() {
            if (! this.steps[this.step]) {
                return false;
            }

            for (const field of this.steps[this.step]) {
                if (this.validationResult[field]) {
                    return false;
                }
            }
            return true;
        },
        entireFormIsValid() {
            for (const field in this.fieldTypes) {
                if (this.validationResult[field]) {
                    return false;
                }
            }
            if (! this.hasApprovedGuidelines) {
                return false;
            }
            return true;
        },
        validationResult() {
            const result = validate(this.formData, this.fieldTypes);
            return result;
        },
        commentTypeConfig() {
            const cnf = state.merchantConfig.comments[this.commentType] || {
                requiredRatings: [],
                optionalRatings: [],
            };
            return cnf;
        },
        steps() {
            const cnf = this.commentTypeConfig;

            if (this.parentCommentId) {
                const steps = [
                    [
                        'text',
                    ],
                ];
                if (cnf.maxFiles > 0 && cnf.maxFileSize > 0) {
                    steps[0].push('images');
                }
                return steps;
            }
            const steps = [
                [
                    'text',
                ],
                [],
            ];


            if (this.commentType !== 'social_post' && this.commentType !== 'product_discussion') {
                steps[1].unshift('title');
            }

            for (const rt of cnf.requiredRatings) {
                if (rt === 'perceived_size' && ! this.productHasSize) {
                    continue;
                }
                steps[0].unshift('ratings.' + rt);
            }

            if (this.selectableTargets && this.selectableTargets.length > 1) {
                steps[0].unshift('target');
            }

            for (const rt of cnf.optionalRatings) {
                if (rt === 'perceived_size' && ! this.productHasSize) {
                    continue;
                }
                steps[1].push('ratings.' + rt);
            }

            if (cnf.maxFiles > 0 && cnf.maxFileSize > 0) {
                steps[0].push('images');
            }

            if (cnf.allowProsAndCons) {
                steps[1].push('pros');
                steps[1].push('cons');
            }

            if (cnf.allowPrice) {
                steps[0].push('price');
            }

            if (cnf.allowLocation) {
                steps[0].push('location');
            }

            if (steps[1].length === 1) {
                steps[0].push(steps.pop()[0]);
            } else if (steps[1].length === 0) {
                steps.pop();
            }

            return steps;
        },
        fieldTypes() {
            const cnf = this.commentTypeConfig;

            const types = {
                target: {
                    type: 'select',
                    params: {
                        required: this.commentType !== 'marketplace' && this.commentType !== 'site_review',
                        options: this.selectableTargets.map(t => ({ value: t.id, label: t.name, imageUrl: t.imageUrl })),
                        placeholder: t('customer_attributes_editor.placeholder'),
                    },
                },
                name: {
                    type: 'text',
                    params: {
                        required: !this.profile && !cnf.skipGuestName,
                        minLength: 2,
                        maxLength: 50,
                    },
                },
                email: {
                    type: 'email',
                    params: {
                        required: !this.profile && !cnf.skipGuestEmail,
                        minLength: 2,
                        maxLength: 50,
                    },
                },
                title: {
                    type: 'text',
                    params: {
                        required: false,
                    },
                },
                location: {
                    type: 'text',
                    params: {
                        required: false,
                    },
                },
                price: {
                    type: 'text',
                    params: {
                        required: false,
                    },
                },
                text: {
                    type: 'textarea',
                    params: {
                        required: cnf.minTextLength > 0,
                        minLength: cnf.minTextLength,
                        maxLength: cnf.maxTextLength,
                    },
                },
                pros: {
                    type: 'stringlist',
                    params: {
                        required: false,
                        maxItemLength: MAX_PROCON_LENGTH,
                        maxItems: MAX_PROS_OR_CONS,
                    },
                },
                cons: {
                    type: 'stringlist',
                    params: {
                        required: false,
                        maxItemLength: MAX_PROCON_LENGTH,
                        maxItems: MAX_PROS_OR_CONS,
                    },
                },
                images: {
                    type: 'imagelist',
                    subtitle: advancedString(t('comments.maximum_file_size'), {
                        maxFileSize: hrFileSize(cnf.maxFileSize),
                        maxCombinedFileSize: hrFileSize(cnf.maxCombinedFileSize),
                    }),
                    params: {
                        maxFiles: cnf.maxFiles,
                        maxFileSize: cnf.maxFileSize,
                        maxCombinedFileSize: cnf.maxCombinedFileSize,
                        allowedExtensions: cnf.allowedExtensions,
                    },
                },
            };

            if (!this.parentCommentId) {
                for (const type of cnf.requiredRatings) {
                    types['ratings.' + type] = {
                        type: 'rating',
                        params: {
                            ratingType: type,
                            required: true,
                            min: 1,
                            max: 5,
                        },
                    };
                }

                for (const type of cnf.optionalRatings) {
                    types['ratings.' + type] = {
                        type: 'rating',
                        params: {
                            ratingType: type,
                            required: false,
                            min: 1,
                            max: 5,
                        },
                    };
                }
            }

            return types;
        },
        fields() {
            return this.steps[this.step];
        },
        profile () {
            if (this.tokenProfile) {
                return this.tokenProfile;
            }
            return state.profile;
        },
     },
};
</script>